import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import http from "../../Common/http-common";
import * as Comp from "../../Common/CommonComponents.js";
import { c_API_IP } from "../../Common/commonURL";

const ExpensesAttachmentModal = ({ show, onHide, source, userId, readOnly = false, onChanged }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFiles, setNewFiles] = useState([]);

  // =========================
  // LOAD ATTACHMENTS
  // =========================
  const loadData = async () => {
    if (!source) return;

    setLoading(true);
    try {
      const res = await http.get(
        "api/expenses/GetExpensesAttachment?source=" + source
      );
      setList(res.data || []);
    } catch {
      toast.error("Failed to load attachments");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) loadData();
  }, [show, source]);

  useEffect(() => {
    if (!show) {
      setNewFiles([]);
    }
  }, [show]);

  // =========================
  // DELETE
  // =========================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this attachment?")) return;

    try {
      await http.post("api/expenses/DeleteAttachment", {
        ATTACHMENT_ID: id,
        UPDATE_ID: userId
      });

      toast.success("Deleted");
      await loadData();
      if (onChanged) onChanged();
    } catch {
      toast.error("Delete failed");
    }
  };

  // =========================
  // SELECT FILE
  // =========================
  const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp", "image/avif", "image/tiff", "image/heic", "image/heif", "image/svg+xml"];

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const valid = [];

    for (let f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        toast.error(`"${f.name}" is not allowed. Only PDF and image files are accepted.`);
        continue;
      }
      if (f.size > 4 * 1024 * 1024) {
        toast.error("Max 4MB per file");
        continue;
      }
      valid.push(f);
    }

    setNewFiles((prev) => [...prev, ...valid]);
  };

  // =========================
  // UPLOAD
  // =========================
  const handleUpload = async () => {
    if (newFiles.length === 0) return;

    try {
      for (let f of newFiles) {
        const formData = new FormData();
        formData.append("file", f);
        formData.append("EXPENSES_DETAILS_ID", source);
        formData.append("USER_ID", userId);

        await http.post(
          "api/expenses/UploadExpensesAttachment",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      toast.success("Uploaded successfully");
      setNewFiles([]);
      await loadData();
      if (onChanged) onChanged();
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>

      <Modal.Header>
        <div style={{ backgroundColor: "#FF8C00", width: "100%" }}>
          <Modal.Title style={{ textAlign: "left", marginLeft: "5px", fontSize: "1.3rem" }}>
            Attachment
          </Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body>

        {/* TABLE */}
        <table
          style={{
            width: "100%",
            tableLayout: "fixed",
            borderCollapse: "collapse",
            border: "1px solid #dee2e6",
          }}
        >
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={{ width: "40px", padding: "8px", border: "1px solid #dee2e6" }}>#</th>
              <th style={{ padding: "8px", border: "1px solid #dee2e6" }}>Filename</th>
              <th style={{ width: "100px", textAlign: "center", padding: "8px", border: "1px solid #dee2e6" }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "12px", border: "1px solid #dee2e6" }}>
                  Loading...
                </td>
              </tr>
            )}

            {!loading && list.length === 0 && (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", padding: "12px", border: "1px solid #dee2e6" }}>
                  No attachment found
                </td>
              </tr>
            )}

            {!loading &&
              list.map((f, i) => (
                <tr
                  key={i}
                  style={{
                    background: "#fafafa",
                  }}
                >
                  <td style={{ textAlign: "center", padding: "10px", border: "1px solid #dee2e6" }}>
                    {i + 1}
                  </td>

                  <td
                    style={{
                      padding: "10px",
                      border: "1px solid #dee2e6",
                      wordBreak: "break-all",
                    }}
                  >
                    {f.FILENAME_ORI}
                  </td>

                  <td style={{ textAlign: "center", padding: "10px", border: "1px solid #dee2e6" }}>
                    <a
                      href={`${c_API_IP}api/expenses/ViewAttachment?attachmentId=${f.ATTACHMENT_ID}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ marginRight: "12px", color: "#0d6efd" }}
                      title="View"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </a>

                    {!readOnly && (
                      <FontAwesomeIcon
                        icon={faTrashCan}
                        style={{ color: "red", cursor: "pointer" }}
                        title="Delete"
                        onClick={() => handleDelete(f.ATTACHMENT_ID)}
                      />
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* UPLOAD SECTION - hidden when readOnly */}
        {!readOnly && (
          <div
            style={{
              marginTop: "14px",
              marginBottom: "20px",
              padding: "12px",
              border: "1px dashed #ccc",
              borderRadius: "6px"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <div style={{ fontWeight: "500" }}>Add Attachment</div>

              <label style={{ cursor: "pointer", color: "#007bff" }}>
                + Select File
                <input
                  key={source}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.avif,.tif,.tiff,.heic,.heif,.svg"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleFileSelect(e);
                    e.target.value = null;
                  }}
                />
              </label>
            </div>

            {newFiles.length > 0 && (
              <div style={{ marginTop: "12px" }}>
                {newFiles.map((f, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "right",
                      fontSize: "13px",
                      padding: "4px 0"
                    }}
                  >
                    <span style={{ wordBreak: "break-all" }}>{f.name}</span>

                    <span
                      style={{
                        color: "#dc3545",
                        cursor: "pointer",
                        fontWeight: "bold",
                        marginLeft: "10px"
                      }}
                      onClick={() =>
                        setNewFiles(prev => prev.filter((_, index) => index !== i))
                      }
                    >
                      ✕
                    </span>
                  </div>
                ))}

                <Comp.Button
                  id="btnUpload"
                  onClick={handleUpload}
                  style={{ marginTop: "10px" }}
                >
                  Upload
                </Comp.Button>
              </div>
            )}
          </div>
        )}

        {/* CLOSE BUTTON */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Comp.Button id="btnClose" onClick={onHide}>
            CLOSE
          </Comp.Button>
        </div>

      </Modal.Body>
    </Modal>
  );
};

export default ExpensesAttachmentModal;
